// ============================================================
// MOONSHINE MASTERS — moonshineWoods.js        THE SHARED WOODS (co-op step 3)
//
// caP'n, 2026-10-24: "go <3" - the same beasts and the same gather spots for everyone on the server.
//
//   games/moonshine/servers/{sid}/woods/picked/{nodeId}  { at }          a spot someone picked (server clock): it's
//                                                                          gone for EVERYONE until it grows back
//   games/moonshine/servers/{sid}/woods/beasts           { k, b, ts }    THE KEEPER runs the beasts (the same keeper as
//                                                                          the yard) and sends where they are:
//                                                                          "x,z,yaw,chasing;..." - 5 a second while one
//                                                                          is chasing, once a second while they wander
//   games/moonshine/servers/{sid}/woods/caught/{uid}     { at }          the keeper's beast caught YOU: you drop your
//                                                                          satchel and wake up in the yard
// ⚑ ONE PACK: the keeper's beasts chase whoever's nearest and out past the lanterns - her or any of you.
// ⚑ NEVER STUCK: if the keeper's pack goes quiet (her tab asleep, her connection gone) your own woods take over
//   again after BEAST_STALE_MS - the solo beasts, exactly as before - and hand back when she's back.
// ⚑ NEVER IN THE RENDER LOOP: listeners write refs; the frame loop reads them (ie-realtime-perf).
// ⚑ A PICK IS CLAIMED, NOT ASSUMED (2026-10-25, the pinned same-instant bug): claimPick() writes woods/picked/{node} in a
//   TRANSACTION that only commits if the spot has grown back; only the one who wins is handed the item. Two friends grabbing
//   the same spot in the same instant: one gets it, the other sees it vanish. Offline / no answer in CLAIM_WAIT_MS: you get it
//   (never stranded - the solo woods are the fallback, as everywhere else).
// ⚠ PLACEHOLDER (scaffolding first): the umbrella security posture, like the yard.
// ============================================================
import { useEffect, useRef } from "react";
import { ref, set, remove, onValue, runTransaction, serverTimestamp } from "firebase/database";
import { db } from "./firebase";
import { serverNow } from "./ieSocial";
import { MOONSHINE_BASE } from "./moonshineOnline";

export const woodsPath = (sid) => `${MOONSHINE_BASE}/servers/${sid}/woods`;
export const BEAST_SEND_CHASE_MS = 200;   // ⟵ DIAL  the keeper's pack, while one is chasing
export const BEAST_SEND_IDLE_MS = 1000;   // ⟵ DIAL  ... while they only wander
export const BEAST_STALE_MS = 3000;       // ⟵ DIAL  the keeper's pack this quiet: your own woods take over
export const CLAIM_WAIT_MS = 2500;       // ⟵ DIAL  no answer from the server in this long: take it anyway (offline is never a stuck spot)
const CATCH_FRESH_MS = 10000;             // a catch older than this (you were away) doesn't count
const skew = () => serverNow() - Date.now();   // the server's clock minus mine

// ── THE GATHER SPOTS ──
// resolves { won, by }: won = this pick is MINE (the spot had grown back and I got there first); if a friend beat me to it,
// `by` is their name (the winner writes it with the pick: { at, n }), for a few friendly words - "Angel got there first."
export function claimPick(sid, nodeId, respawnMs, myName) {
  if (!sid || !nodeId) return Promise.resolve({ won: true, by: null });
  return new Promise((resolve) => {
    let done = false;
    const fin = (v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } };
    const t = setTimeout(() => fin({ won: true, by: null }), CLAIM_WAIT_MS);
    runTransaction(ref(db, `${woodsPath(sid)}/picked/${nodeId}`),
      (cur) => (cur && Number.isFinite(cur.at) && serverNow() - cur.at < respawnMs ? undefined : { at: serverTimestamp(), n: String(myName || "").slice(0, 24) }),
      { applyLocally: false })
      .then((r) => {
        const won = !!(r && r.committed), v = !won && r && r.snapshot && r.snapshot.val ? r.snapshot.val() : null;
        fin({ won, by: v && typeof v.n === "string" && v.n ? v.n.slice(0, 24) : null });
      })
      .catch(() => fin({ won: true, by: null }));
  });
}
// THE SHARED WISP (2026-10-30, caP'n: "yes" - friends share one Stray Wisp, like the gather spots): woods/wisp = { n, at, by }.
// n = how many the crew has caught; the live wisp is wispAt(n) for everyone. Catching = a transaction that moves n on by one only
// if nobody beat you to it (the loser hears who). No answer in CLAIM_WAIT_MS = you get it (offline is never a stuck wisp).
export function claimWisp(sid, n, myName) {
  if (!sid) return Promise.resolve({ won: true, by: null });
  return new Promise((resolve) => {
    let done = false;
    const fin = (v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } };
    const t = setTimeout(() => fin({ won: true, by: null }), CLAIM_WAIT_MS);
    runTransaction(ref(db, `${woodsPath(sid)}/wisp`),
      (cur) => (((cur && Number.isFinite(cur.n) ? cur.n : 0) === n) ? { n: n + 1, at: serverTimestamp(), by: String(myName || "").slice(0, 24) } : undefined),
      { applyLocally: false })
      .then((r) => {
        const won = !!(r && r.committed), v = !won && r && r.snapshot && r.snapshot.val ? r.snapshot.val() : null;
        fin({ won, by: v && typeof v.by === "string" && v.by ? v.by : null });
      })
      .catch(() => fin({ won: true, by: null }));
  });
}
// the crew's wisp count lands in WILD.wispN (null = solo / nothing heard yet)
export function useSharedWisp(sid, WILD) {
  useEffect(() => {
    WILD.wispN = null;
    if (!sid) return undefined;
    const off = onValue(ref(db, `${woodsPath(sid)}/wisp`), (snap) => { const v = snap.val(); WILD.wispN = v && Number.isFinite(v.n) && v.n >= 0 ? Math.floor(v.n) : 0; }, () => {});
    return () => { off(); WILD.wispN = null; };
  }, [sid, WILD]);
}
// everyone's picks land in `pickedAt` (World3D's WILD.pickedAt) on MY clock
export function useSharedPicks(sid, pickedAt) {
  useEffect(() => {
    if (!sid) return undefined;
    return onValue(ref(db, `${woodsPath(sid)}/picked`), (snap) => {
      const v = snap.val() || {}, sk = skew();
      for (const [id, r] of Object.entries(v)) if (r && Number.isFinite(r.at)) { const local = r.at - sk; if (!(pickedAt[id] >= local)) pickedAt[id] = local; }
    }, () => {});
  }, [sid, pickedAt]);
}

// ── THE PACK ──
export function sendBeasts(sid, uid, st) {
  if (!sid || !uid) return;
  const b = st.map((x) => `${x.x.toFixed(2)},${x.z.toFixed(2)},${x.yaw.toFixed(2)},${x.chasing ? 1 : 0}`).join(";");
  set(ref(db, `${woodsPath(sid)}/beasts`), { k: uid, b, ts: serverTimestamp() }).catch(() => {});
}
// a guest's view of the keeper's pack: feed.current = { k, at (my clock, when it arrived), list: [[x,z,yaw,chasing]] }
export function useBeastFeed(sid, on) {
  const feed = useRef(null);
  useEffect(() => {
    feed.current = null;
    if (!sid || !on) return undefined;
    return onValue(ref(db, `${woodsPath(sid)}/beasts`), (snap) => {
      const v = snap.val();
      if (!v || typeof v.b !== "string" || !v.b) return;
      if (Number.isFinite(v.ts) && serverNow() - v.ts > BEAST_STALE_MS) return;     // an old pack left behind by a keeper who's gone: not news (else a new arrival is chased by ghosts for 3 s)
      const list = v.b.split(";").map((r) => r.split(",").map(Number)).filter((r) => r.length === 4 && r.every(Number.isFinite));
      feed.current = { k: v.k, at: Date.now(), list };
    }, () => {});
  }, [sid, on]);
  return feed;
}
export function sendCatch(sid, uid) {
  if (!sid || !uid) return;
  set(ref(db, `${woodsPath(sid)}/caught/${uid}`), { at: serverTimestamp() }).catch(() => {});
}
// the keeper's beast caught ME
export function useCaught(sid, uid, onCaught) {
  const cbRef = useRef(onCaught); cbRef.current = onCaught;
  useEffect(() => {
    if (!sid || !uid) return undefined;
    const r = ref(db, `${woodsPath(sid)}/caught/${uid}`);
    return onValue(r, (snap) => {
      const v = snap.val();
      if (!v) return;
      remove(r).catch(() => {});
      if (Number.isFinite(v.at) && serverNow() - v.at < CATCH_FRESH_MS) cbRef.current();
    }, () => {});
  }, [sid, uid]);
}
